import { supabase } from './supabase';
import type { GrammarConfig } from './grammar/topics';
import type { Cue } from './subtitles';
import type { Assignment, AssignmentKind, Classroom, Member, Pass, Progress, StudentAccount } from './types';

// Thin wrappers. Every permission lives in RLS (supabase/migrations/
// 20261002_classroom.sql); nothing here is a security boundary.

function check<T>(res: { data: T | null; error: { message: string } | null }): T {
  if (res.error) throw new Error(res.error.message);
  return res.data as T;
}

export async function fetchMyStudentAccount(userId: string): Promise<StudentAccount | null> {
  return check(await supabase.from('student_accounts').select('*').eq('user_id', userId).maybeSingle());
}

export async function fetchClassrooms(): Promise<Classroom[]> {
  return check(await supabase.from('classrooms').select('*').order('created_at'));
}

export async function createClassroom(name: string, language = 'es', nativeLanguage = 'en'): Promise<Classroom> {
  // teacher_id and join_code are filled by the trigger, not trusted from here.
  return check(await supabase.from('classrooms')
    .insert({
      name, language, native_language: nativeLanguage, join_code: 'pending',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
    .select().single());
}

/** Record the teacher's time zone on a class made before classes had one. */
export async function setClassroomTimezone(id: string, timezone: string): Promise<void> {
  check(await supabase.from('classrooms').update({ timezone }).eq('id', id));
}

export async function renameClassroom(id: string, name: string): Promise<void> {
  check(await supabase.from('classrooms').update({ name }).eq('id', id));
}

export async function setClassroomLanguages(id: string, language: string, nativeLanguage: string): Promise<void> {
  check(await supabase.from('classrooms').update({ language, native_language: nativeLanguage }).eq('id', id));
}

/** The class's meeting link and weekly time (nulls clear them). */
/** Open the class ocean without homework first (true), or only after an assignment each day (false). */
export async function setOceanFree(id: string, free: boolean): Promise<void> {
  check(await supabase.from('classrooms').update({ ocean_free: free }).eq('id', id));
}

export async function setClassroomMeeting(id: string, m: { meet_url: string | null; meet_day: number | null; meet_time: string | null; meet_minutes: number | null }): Promise<void> {
  check(await supabase.from('classrooms').update(m).eq('id', id));
}

/**
 * Delete a class. The database cascades to its roster, assignments, progress
 * and passes. Student ACCOUNTS survive — they belong to the student, and may be
 * in other classes — they just stop being in this one.
 */
export async function deleteClassroom(id: string): Promise<void> {
  check(await supabase.from('classrooms').delete().eq('id', id));
}

export async function regenerateJoinCode(id: string): Promise<string> {
  return check(await supabase.rpc('regenerate_join_code', { cid: id }));
}

export async function joinClassroom(code: string): Promise<Classroom> {
  return check(await supabase.rpc('join_classroom', { code }));
}

/** A student's share of each story's reading time, in this class (stories that need reading time). */
export async function setStudentReadTime(classroomId: string, studentId: string, pct: number): Promise<void> {
  check(await supabase.rpc('set_student_read_time', { cid: classroomId, sid: studentId, pct }));
}

/** The signed-in student's own membership row (their reading-time share). */
export async function fetchMyMembership(classroomId: string, studentId: string): Promise<Member | null> {
  return check(await supabase.from('classroom_members').select('*')
    .eq('classroom_id', classroomId).eq('student_id', studentId).maybeSingle());
}

export async function fetchMembers(classroomId: string): Promise<{ members: Member[]; students: StudentAccount[] }> {
  // The class's test student (student view) isn't one of the class: never on the roster, in progress or results.
  const members = (check(await supabase.from('classroom_members').select('*').eq('classroom_id', classroomId)) as Member[]).filter(m => !m.is_test);
  const ids = members.map(m => m.student_id);
  const students: StudentAccount[] = ids.length === 0 ? [] : check(
    await supabase.from('student_accounts').select('*').in('user_id', ids).order('display_name'),
  );
  return { members, students };
}

export async function removeMember(classroomId: string, studentId: string): Promise<void> {
  check(await supabase.from('classroom_members').delete()
    .eq('classroom_id', classroomId).eq('student_id', studentId));
}

export async function fetchAssignments(classroomId: string): Promise<Assignment[]> {
  return check(await supabase.from('assignments').select('*')
    .eq('classroom_id', classroomId)
    .order('week_start', { ascending: false })
    .order('created_at', { ascending: false }));
}

export type AssignmentDraft = {
  classroom_id: string;
  kind: AssignmentKind;
  title: string;
  instructions: string | null;
  week_start: string;
  draft: boolean;
  publish_on: string | null;
  due_date: string | null;
  video_id: string | null;
  story_text: string | null;
  read_wpm: number;
  required_count: number;
  one_per_day: boolean;
  subtitles: Cue[] | null;
  subtitles_status: 'ok' | 'manual' | 'none' | 'wrong_language' | null;
  subtitles_checked_at: string | null;
  grammar: GrammarConfig | null;
  sd_per_pass: number;
  sd_on_complete: number;
  images?: { path: string; caption?: string; w?: number; h?: number }[];
};

export async function saveAssignment(draft: AssignmentDraft, id?: string): Promise<Assignment> {
  if (id) {
    return check(await supabase.from('assignments').update(draft).eq('id', id).select().single());
  }
  return check(await supabase.from('assignments').insert(draft).select().single());
}

export async function deleteAssignment(id: string): Promise<void> {
  check(await supabase.from('assignments').delete().eq('id', id));
}

/** RLS narrows this to the caller's own rows (student) or their classes' (teacher). */
export async function fetchProgress(assignmentIds: string[]): Promise<Progress[]> {
  if (assignmentIds.length === 0) return [];
  return check(await supabase.from('assignment_progress').select('*').in('assignment_id', assignmentIds));
}

export async function fetchPasses(assignmentIds: string[]): Promise<Pass[]> {
  if (assignmentIds.length === 0) return [];
  return check(await supabase.from('assignment_passes').select('*')
    .in('assignment_id', assignmentIds).order('n'));
}

export type ActivitySlice = {
  seconds?: number;
  lookups?: number;
  videoWatched?: number;
  videoDuration?: number | null;
  complete?: boolean;
  /** Grammar lessons: the score, sent with the completing call. */
  correct?: number;
  total?: number;
};

export async function logActivity(assignmentId: string, s: ActivitySlice): Promise<Progress> {
  return check(await supabase.rpc('log_assignment_activity', {
    p_assignment_id: assignmentId,
    p_seconds: Math.round(s.seconds ?? 0),
    p_lookups: s.lookups ?? 0,
    p_video_watched: Math.round(s.videoWatched ?? 0),
    p_video_duration: s.videoDuration ? Math.round(s.videoDuration) : null,
    p_complete: !!s.complete,
    ...(s.total !== undefined ? { p_correct: s.correct ?? 0, p_total: s.total } : {}),
  }));
}

// ── Edge function: student accounts ─────────────────────────────────────────

async function callAccounts(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.functions.invoke('student-accounts', { body });
  if (error) {
    let message = 'Something went wrong. Try again.';
    try {
      const b = await (error as { context?: Response }).context?.json();
      if (b?.error) message = b.error;
    } catch { /* keep generic */ }
    throw new Error(message);
  }
  return data ?? {};
}

export async function studentSignup(p: {
  username: string; password: string; displayName: string; joinCode: string;
}): Promise<{ classroomName: string }> {
  const out = await callAccounts({ action: 'signup', ...p });
  return { classroomName: String(out.classroomName ?? '') };
}

export type CreatedStudent = { displayName: string; username: string; password: string };

/** Make accounts for a list of names and put them in this class (the roster import). */
export async function bulkCreateStudents(classroomId: string, names: string[]): Promise<{
  created: CreatedStudent[]; failed: { displayName: string; reason: string }[];
}> {
  const out = await callAccounts({ action: 'bulk_create', classroomId, students: names.map(displayName => ({ displayName })) });
  return {
    created: (out.created ?? []) as CreatedStudent[],
    failed: (out.failed ?? []) as { displayName: string; reason: string }[],
  };
}

export async function resetStudentPassword(studentId: string, password: string): Promise<void> {
  await callAccounts({ action: 'reset_password', studentId, password });
}

// ── Edge function: the video's own subtitles ────────────────────────────────

export type SubtitleFetch =
  | { status: 'ok'; cues: Cue[]; lang: string }
  | { status: 'none' | 'wrong_language'; available?: string[]; got?: string }
  | { status: 'not_configured' | 'pending' | 'quota' | 'error' };

export async function fetchVideoSubtitles(videoId: string, lang: string): Promise<SubtitleFetch> {
  const { data, error } = await supabase.functions.invoke('fetch-subtitles', { body: { videoId, lang } });
  if (error) return { status: 'error' };
  return data as SubtitleFetch;
}

/** Make sure an assignment has its subtitles: fetched once, saved for everyone. */
export async function ensureAssignmentSubtitles(assignmentId: string): Promise<SubtitleFetch> {
  const { data, error } = await supabase.functions.invoke('fetch-subtitles', { body: { assignmentId } });
  if (error) return { status: 'error' };
  return data as SubtitleFetch;
}

// ── Due-date extensions (20261036) ──────────────────────────────────────────
// A later due date for one student. Students read only their own; teachers their class's.

export type Extension = { assignment_id: string; student_id: string; due_date: string };

export async function fetchExtensions(assignmentIds: string[]): Promise<Extension[]> {
  if (assignmentIds.length === 0) return [];
  const { data, error } = await supabase.from('assignment_extensions').select('assignment_id, student_id, due_date').in('assignment_id', assignmentIds);
  if (error) return [];                                   // before 20261036 is run: no extensions
  return data as Extension[];
}

/** Give a student their own due date for an assignment, or (null) take it away. */
export async function setExtension(assignmentId: string, studentId: string, due: string | null): Promise<void> {
  if (due) check(await supabase.from('assignment_extensions').upsert({ assignment_id: assignmentId, student_id: studentId, due_date: due }));
  else check(await supabase.from('assignment_extensions').delete().eq('assignment_id', assignmentId).eq('student_id', studentId));
}
